import React, { useEffect, useState } from "react";
import { fetchPracticeCallChats, fetchPracticeCallChat, saveWordBankSentence, correctTranscript, savePracticeCallChat } from "../api";
import CallChat, { shortName } from "./CallChat";

/**
 * Modal listing the user's past practice-call chats. Selecting one loads its
 * full transcript into the CallChat viewer, where sentences can be saved to the
 * Word Bank. Works on desktop and mobile.
 */
export default function PastCallChats({ onClose }) {
  const [rows, setRows] = useState(null);     // null = loading
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // { session, partner_username, messages }
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    fetchPracticeCallChats()
      .then(setRows)
      .catch(() => setError("Couldn't load past calls."));
  }, []);

  const openChat = async (row) => {
    setLoadingChat(true);
    setError("");
    try {
      const data = await fetchPracticeCallChat(row.session);
      setSelected({ ...row, messages: data.messages || [] });
    } catch {
      setError("Couldn't load that chat.");
    } finally {
      setLoadingChat(false);
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "");

  return (
    <div className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="surface-glass rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg h-[88vh] sm:h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 shrink-0" style={{ borderBottom: "1px solid var(--panel-line)" }}>
          <div className="flex items-center gap-2">
            {selected && (
              <button onClick={() => setSelected(null)} aria-label="Back" className="text-dt-parchment-dim hover:text-dt-parchment text-lg leading-none">←</button>
            )}
            <div>
              <h2 className="text-base font-semibold text-dt-parchment leading-tight">
                {selected ? `Chat with ${shortName(selected.partner_name || selected.partner_username) || "partner"}` : "Past call chats"}
              </h2>
              {selected && <p className="text-xs text-dt-parchment-dim leading-tight">{fmtDate(selected.updated_at)}</p>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-dt-parchment-dim hover:text-dt-parchment text-xl leading-none p-1">✕</button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {error && <p className="text-sm mb-3" style={{ color: "var(--tag-rose)" }}>{error}</p>}

          {selected ? (
            <CallChat
              messages={selected.messages}
              partnerName={selected.partner_name || selected.partner_username}
              onSaveMessage={(m) => saveWordBankSentence({ arabic: m.arabic, translation: m.translation || "" })}
              onCorrectMessage={async (index, m) => {
                const { arabic, translation } = await correctTranscript(m.arabic);
                setSelected((prev) => {
                  const msgs = prev.messages.map((x, i) => (i === index ? { ...x, arabic, translation: translation || x.translation } : x));
                  savePracticeCallChat(prev.session, msgs).catch(() => {});
                  return { ...prev, messages: msgs };
                });
              }}
            />
          ) : rows === null ? (
            <p className="text-sm text-dt-parchment-dim text-center py-8">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-dt-parchment-dim text-center py-8">No past call chats yet.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <button
                  key={r.session}
                  onClick={() => openChat(r)}
                  disabled={loadingChat}
                  className="surface-glass surface--interactive w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-dt-parchment">{shortName(r.partner_name || r.partner_username) || "Partner"}</p>
                    <p className="text-xs text-dt-parchment-dim">{fmtDate(r.updated_at)}</p>
                  </div>
                  <span className="text-xs text-dt-parchment-dim shrink-0">{r.message_count} msg{r.message_count === 1 ? "" : "s"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
